import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { field, FilterContent, NewFieldStyles } from "../../../Definitions";
import { useFeedcurveOptions, useGroupOptions, useMilestoneOptions, useSectionOptions, useSystemOptions } from "../../_Components";
import { colors, glyphs, useMediaQuery } from "../../../Styles";
import { NewSelect } from "../..";
import { getLabelFromKey, ReportTemplate, ValueType } from "../../../Definitions/valueTypes";
import { createDisplayTypesFromOptions } from "../../../Definitions/filterTypes";

interface Lvl2_Props {
  templateData?: ReportTemplate
  onTemplateDataChange: (data: ReportTemplate) => void
}

export default function TemplateModalLvl2({ templateData, onTemplateDataChange }:Lvl2_Props) {
  const {t} = useTranslation()

  const groupOptions = useGroupOptions();
  const milestoneOptions = useMilestoneOptions();
  const systemOptions = useSystemOptions();
  const { feedcurveOptions } = useFeedcurveOptions();
  const { sectionOptions } = useSectionOptions();

  const [selectFields, setSelectFields] = React.useState<field[]>([{ id: -1 }]);
    const mobileXs = useMediaQuery(`(max-width: 793px)`);
  
  // #region | Select Parameters
  const selectParameters = createDisplayTypesFromOptions({groupOptions, feedcurveOptions, milestoneOptions, systemOptions, sectionOptions});
  

  const labelToKeyMap: Record<string, "animal_number" | "feedCurveId" | "animal_group_id" | "system_id" | "milestone_id"> = {
    animal_number: "animal_number",
    feedCurve: "feedCurveId",
    group: "animal_group_id",
    system: "system_id",
    milestone: "milestone_id"
  };

  const filterParamOptions = selectParameters.map((type) => ({
    label: t(`params.${type.label}`, {defaultValue: type.label}), 
    id: type.id
  }));
  // #endregion

  // If reportData already have values to show
  useEffect(() => {
    if (templateData && templateData.filterParameter && templateData.filterParameter.length && templateData.filterParameter[0].length) {
      const updatedFields: field[] = templateData.filterParameter[0].map((param) => {
        const label = getLabelFromKey(param.key);
        const id = selectParameters.find((p) => p.label === label)?.id ?? -1;
        return { id, value: param.value };
      });
      updatedFields.push({ id: -1 });
      setSelectFields(updatedFields);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateData?.filterParameter]);
  
  // Handle filter type change
  const handleFilterTypeChange = (index: number, id: number) => {
    const updatedSelectFields = [...selectFields]
    ;
    if (updatedSelectFields[index].id === -1) {
      updatedSelectFields.push({ id: -1 });
    }
    updatedSelectFields[index] = { id, value: undefined };

    const selectedParameter = selectParameters.find((param) => param.id === id);
    if (selectedParameter) {
      const updatedFilterParameter = templateData?.filterParameter?.[0] ? [...templateData.filterParameter[0]] : [];
      updatedFilterParameter[index] = {key: selectedParameter.key, value: []}
      onTemplateDataChange({...templateData, templateId: templateData?.templateId || 0, filterParameter: [updatedFilterParameter]});
    }
    setSelectFields(updatedSelectFields);
  };

  // Handle filter value change
  const handleFilterValueChange = (index: number, values: ValueType[]) => {
    const updatedFilterParameter = Array.isArray(templateData?.filterParameter?.[0]) ? [...templateData.filterParameter[0]] : [];
  
    if (!updatedFilterParameter[index]) return;
  
    updatedFilterParameter[index].value = values;
    onTemplateDataChange({...templateData, templateId: templateData?.templateId || 0, filterParameter: [updatedFilterParameter]});
  };

  // Remove a SelectField by index
  const handleRemoveField = useCallback((index: number) => {
    const updatedSelectFields = [...selectFields];
    updatedSelectFields.splice(index, 1);
    setSelectFields(updatedSelectFields);
  
    const updatedFilterParameter= templateData?.filterParameter?.[0];
    updatedFilterParameter?.splice(index, 1);
    onTemplateDataChange({...templateData, templateId: templateData?.templateId || 0, filterParameter: [(updatedFilterParameter ?? [])] });

  }, [onTemplateDataChange, templateData, selectFields]);

// #region | Rendered Fields
const renderedFields = selectFields.map((field, index) => {
  // Remove already used options from optionslist
  const selectedIds = selectFields
  .filter((f, i) => i !== index && f.id !== undefined)
  .map((f) => f.id); 
  const availableOptions = filterParamOptions.filter((option) => !selectedIds.includes(option.id));

  return (
    <div key={field.id !== undefined ? `${field.id}-${index}` : index} style={{ 
      width: "100%", 
      display: "flex", 
      flexDirection: "column", 
      gap: "10px", 
      marginBottom: index === selectFields.length - 1 ? "0px" : "30px"
    }}>
      <div style={{ display: "flex", flexDirection: "row", gap: "20px", alignItems: "end", justifyContent: "space-between" }}>
        <NewSelect
          placeholder={t("select", {defaultValue: "select"})}
          key={index+String(field.id)}
          options={availableOptions}
          onChange={(option) => {
            option?.id && handleFilterTypeChange(index,  Number(option.id));
          }}
          fieldOverrides={{
            fieldWidth: mobileXs ? "250px" : "280px",
            fieldStyle: NewFieldStyles.blueDarkSecondary
          }}
          valueOverrides={{
            defaultValue: filterParamOptions.find((parameter) => {return parameter.id === field.id})
          }}
        />
        {field.id !== -1 && ( <>
          <div style={{cursor: "pointer"}} onClick={() => handleRemoveField(index)}>
            <glyphs.Delete size="25px"/>
          </div>
        </> )}
      </div>
      {/* Content based on selected filter parameter */}
      {field.id !== -1 && (
        <div style={{    
          width: "100%",
          height: "fit-content",
          boxSizing: "border-box",
          backgroundColor: colors.greyLightSecondary,
          padding: mobileXs? "10px 5px" : "10px",
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <FilterContent 
            filter={field} 
            index={index} 
            filterParameterTypes={selectParameters} 
            handleFilterValueChange={(values) => {handleFilterValueChange(index, values)}}
          />
        </div>
      )}
    </div>
  )
})
// #endregion

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "start",
      flexDirection: "column",
      boxSizing: "border-box"
    }}>
      {renderedFields}
    </div>
  )
}